import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

import AnalyticsExportDialog from 'components/AnalyticsExportDialog'
import AnalyticsFilters from 'components/AnalyticsFilters'
import AnalyticsNavigation from 'components/AnalyticsNavigation'
import AnalyticsSectionContent from 'components/AnalyticsSectionContent'
import AnalyticsSkeleton from 'components/AnalyticsSkeleton'
import Button from 'components/Form/Button'
import useController from './controller'
import {
  DataQualityNote,
  Header,
  HeaderCopy,
  HeaderTools,
  Page,
  PageDescription,
  PageTitle,
  SectionContext,
  SectionDescription,
  SectionTitle,
  State,
  StateAction,
  StateText,
  StateTitle,
} from './styled'

const certificateColumns = [
  ['userName', 'User'], ['userEmail', 'Email'], ['courseTitle', 'Course'], ['program', 'Program'],
  ['trainer', 'Trainer'], ['area', 'Area'], ['type', 'Certificate Type'], ['status', 'Status'], ['issuedAt', 'Issued Date'], ['certificateId', 'Certificate ID'],
]

const exportCertificateXlsx = (rows = [], filename = 'certificate-report.xlsx') => {
  const data = rows.map(row => Object.fromEntries(certificateColumns.map(([key, label]) => [label, key === 'issuedAt' && row[key] ? new Date(row[key]) : row[key] ?? ''])))
  const worksheet = XLSX.utils.json_to_sheet(data)
  worksheet['!cols'] = certificateColumns.map(([, label]) => ({ wch: Math.min(32, Math.max(label.length + 2, ...data.map(row => String(row[label] || '').length + 2))) }))
  worksheet['!autofilter'] = { ref: worksheet['!ref'] }
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 }
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates')
  const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellDates: true })
  saveAs(new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename)
}

const exportSurveyXlsx = (data = {}, filename = 'survey-report.xlsx') => {
  const workbook = XLSX.utils.book_new()
  const summary = (data.surveyRows || []).map(row => ({
    'Date From': data.range?.from ? new Date(data.range.from) : '',
    'Date To': data.range?.to ? new Date(data.range.to) : '',
    Survey: row.survey || '',
    Area: row.area || 'All',
    Course: row.course || '',
    Views: row.views || 0,
    Starts: row.starts || 0,
    Completions: row.completions || 0,
    Responses: row.responses || 0,
    'Unique Participants': row.uniqueParticipants || 0,
    'Completion Rate': Number(row.completionRate || 0) / 100,
    'Abandonment Rate': Number(row.abandonmentRate || 0) / 100,
    'Average Completion Time (seconds)': row.averageCompletionTime ?? '',
  }))
  const questions = (data.questionAnalytics || []).map(row => ({
    Survey: row.survey || '',
    Question: row.question || '',
    Type: row.type || '',
    Responses: row.responseCount || 0,
    Average: row.average ?? '',
    'Open Text Values Included': 'No',
  }))
  const answers = (data.answerDistribution || []).map(row => ({
    Survey: row.survey || '',
    Question: row.question || '',
    Type: row.type || '',
    Option: row.option || '',
    Count: row.count || 0,
    Percentage: Number(row.percentage || 0) / 100,
  }))
  ;[
    ['Survey Summary', summary],
    ['Question Analytics', questions],
    ['Answer Distribution', answers],
  ].forEach(([name, rows]) => {
    const worksheet = XLSX.utils.json_to_sheet(rows)
    worksheet['!autofilter'] = worksheet['!ref'] ? { ref: worksheet['!ref'] } : undefined
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 }
    XLSX.utils.book_append_sheet(workbook, worksheet, name)
  })
  const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellDates: true })
  saveAs(new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename)
}

export default function Analytics() {
  const [exportOpen, setExportOpen] = useState(false)
  const {
    accessStatus,
    data,
    filterOptions,
    filters,
    formatDate,
    formatDateTime,
    formatDuration,
    formatNumber,
    formatPercent,
    language,
    loadReportSection,
    locale,
    resetFilters,
    retry,
    section,
    sections,
    selectSection,
    setFilter,
    setPage,
    setSort,
    status,
    t,
  } = useController()
  const visibleStatus = accessStatus === 'ready' ? status : accessStatus

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <PageTitle>{t('analytics_title')}</PageTitle>
          <PageDescription>{t('analytics_description')}</PageDescription>
        </HeaderCopy>
        {accessStatus === 'ready' ? (
          <HeaderTools>
            <Button color="blue" disabled={!['ready', 'empty'].includes(status)} nospace onClick={() => setExportOpen(true)} width="auto">
              {t('analytics_export_report')}
            </Button>
            {section === 'certificates' ? <Button color="blue" disabled={!data?.certificateRows?.length} nospace onClick={() => exportCertificateXlsx(data.certificateRows)} width="auto">
              {t('analytics_export_certificates_xlsx')}
            </Button> : null}
            {section === 'surveys' ? <Button color="blue" disabled={!data?.surveyRows?.length} nospace onClick={() => exportSurveyXlsx(data)} width="auto">
              {t('analytics_export_surveys_xlsx')}
            </Button> : null}
          </HeaderTools>
        ) : null}
      </Header>

      {accessStatus === 'ready' ? (
        <>
          <AnalyticsNavigation onSelect={selectSection} section={section} sections={sections} t={t} />
          <SectionContext>
            <SectionTitle>{t(`analytics_nav_${section}`)}</SectionTitle>
            <SectionDescription>{t(`analytics_${section}_description`)}</SectionDescription>
          </SectionContext>
          <AnalyticsFilters filterOptions={filterOptions} filters={filters} resetFilters={resetFilters} section={section} setFilter={setFilter} t={t} />
        </>
      ) : null}

      {visibleStatus === 'loading' ? (
        <AnalyticsSkeleton label={t('analytics_loading')} />
      ) : ['error', 'denied', 'empty'].includes(visibleStatus) ? (
        <State urgent={visibleStatus !== 'empty'}>
          <StateTitle>
            {t(visibleStatus === 'denied' ? 'analytics_denied_title' : visibleStatus === 'empty' ? 'analytics_no_data_title' : 'analytics_error_title')}
          </StateTitle>
          <StateText>
            {t(visibleStatus === 'denied' ? 'analytics_denied_description' : visibleStatus === 'empty' ? 'analytics_no_data_description' : 'analytics_error_description')}
          </StateText>
          {visibleStatus === 'error' ? <StateAction onClick={retry} type="button">{t('analytics_retry')}</StateAction> : null}
        </State>
      ) : (
        <>
          {data?.dataQuality?.truncated ? <DataQualityNote role="status">{t('analytics_data_truncated')}</DataQualityNote> : null}
          <AnalyticsSectionContent
            data={data}
            filters={filters}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
            formatDuration={formatDuration}
            formatNumber={formatNumber}
            formatPercent={formatPercent}
            section={section}
            setPage={setPage}
            setSort={setSort}
            t={t}
          />
        </>
      )}

      <AnalyticsExportDialog
        filters={filters}
        filterOptions={filterOptions}
        language={language}
        loadSection={loadReportSection}
        locale={locale}
        onClose={() => setExportOpen(false)}
        open={exportOpen}
        section={section}
        sectionData={data}
        t={t}
      />
    </Page>
  )
}
